/**
 * The onboarding handover (`spec-onboarding-persistent-scene-handover`): the plan map being left is
 * held as a picture until the next plan map has drawn, then dissolves.
 *
 * Held outside React, like the hand-back (`planPoseHandback.ts`): the page's section setter, the plan
 * maps and the layer above the routes (`PlanHandoverLayer`) all reach it, and none of them owns it.
 *
 * - **One hold at a time (R1.2).** `holdScene()` captures the map registered now and holds it, with
 *   a generation. The arriving map announces itself (`announceArrival`) and reports when it has
 *   drawn (`reportArrived`); only the current generation's report releases (Failure States,
 *   Overtaken).
 * - **Bounded (R3.3, R3.4).** No announcement within 250 ms of the hold releases it; nothing is held
 *   longer than 1500 ms from the capture plus one fade, even when a newer join overtakes after the
 *   cap or a map fading in unmounts (the backstop).
 * - **Released at once** (no fade) when the page unmounts, its twin changes, the viewport resizes or
 *   a newer step change is not handed over (R3.6, Failure States).
 * - **An editor map reveals itself** (`spec-onboarding-persistent-scene-handover-glass-align`, R2):
 *   an arriving map that announces with `reveals` stays invisible over the picture until it
 *   arrives, then fades in, and the picture is removed when that fade ends (`revealFor`). A stage
 *   map (the washed plan, Position twin's) announces without it, and its picture fades out over it.
 *
 * Imports no map or 3D library (the motion token alone): the layer that reads it sits in
 * `ProtectedGate`, in the app's main chunk. Nothing here is stored, logged or sent anywhere but one
 * warning when a capture fails (spec, Access and Data Exposure).
 */

import { motion } from '@repo/design-tokens';

/** A picture of the leaving map, and where its canvas sat on the viewport (CSS pixels). */
export interface HeldPicture {
  canvas: HTMLCanvasElement;
  rect: { left: number; top: number; width: number; height: number };
  /**
   * Where the picture is held. Absent: the handover layer over the page, as for a map in a step's
   * pane. An element: inside it, so the picture keeps that element's place under the page's chrome
   * (the onboarding backdrop for the washed plan, `spec-onboarding-persistent-scene-wash`, PR review).
   * A function: found by the layer when it can, so a picture taken before its host exists (the live
   * viewer's Edit, `spec-onboarding-persistent-scene-edit-flight`, R6.1) is held over the page until
   * the host mounts, then inside it.
   */
  host?: HTMLElement | null | (() => HTMLElement | null);
}

/** Where a hold came from: a step change in onboarding, or the live viewer's Edit (R6.5, R6.6). */
export type HoldOrigin = 'step' | 'edit';

/** A hold's own bounds and origin (`spec-onboarding-persistent-scene-edit-flight`, R6.5). */
export interface HoldOptions {
  announceWaitMs?: number;
  capMs?: number;
  origin?: HoldOrigin;
  /** The twin an Edit hold was made for. */
  twinBid?: string;
}

/** How a plan map captures itself, synchronously: the picture, or `null` (nothing held). */
export type CaptureMap = () => HeldPicture | null;

/** What an arriving map reports: it drew and went idle, or its draw failed. */
export type ArrivalOutcome = 'arrived' | 'failed';

/** What the layer and the arriving map read: the held picture, and how it is being let go. */
export interface HandoverSnapshot {
  picture: HeldPicture;
  /** One per captured picture (an overtaken hold keeps it): the layer remounts on a new one. */
  pictureId: number;
  generation: number;
  /** Fading out (or, with `immediate`, to be removed now). */
  releasing: boolean;
  /** Removed at once, without a fade (R3.6; the layer also cuts under reduced motion, R1.4). */
  immediate: boolean;
  /** When the picture was captured, on `performance.now()`'s clock. */
  heldAt: number;
  /** How long this picture may be held, from `heldAt` (`HOLD_CAP_MS` unless the hold set it). */
  capMs: number;
  /** How long a generation of this hold waits for its map to announce itself. */
  announceWaitMs: number;
  origin: HoldOrigin;
  twinBid: string | null;
  /** When its release began, on the same clock; `null` while held. */
  releasedAt: number | null;
  /**
   * The generation whose arriving map announced that it reveals itself over the picture (an
   * editor map, glass-align R2.2); `null`: none has. Such a map's arrival starts its fade-in rather
   * than releasing the picture.
   */
  revealer: number | null;
  /** The generation whose map has started fading in over the picture; `null`: none has. */
  revealGeneration: number | null;
  /** When that fade began, on `heldAt`'s clock; `null` until it does. */
  revealStartedAt: number | null;
}

/** What an arriving map shows: invisible over the picture, fading in, or as it always is. */
export type RevealState = 'hidden' | 'revealing' | 'shown';

/** R3.3: how long a hold waits for an arriving map to announce itself. */
export const ANNOUNCE_WAIT_MS = 250;
/** R3.4: the longest a picture is held, from its capture. */
export const HOLD_CAP_MS = 1500;

let capture: CaptureMap | null = null;
let generation = 0;
let pictureSeq = 0;
let snapshot: HandoverSnapshot | null = null;
let announceTimer: ReturnType<typeof setTimeout> | null = null;
let capTimer: ReturnType<typeof setTimeout> | null = null;
/** The last resort: a hold whose map's fade-in can no longer end it is cut (see `armBackstop`). */
let backstopTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

const DEV = process.env.NODE_ENV !== 'production';
const mark = (name: string) => {
  if (!DEV || typeof performance === 'undefined' || !performance.mark) return;
  try {
    performance.mark(name);
  } catch {
    // A browser without user timing.
  }
};

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const notify = () => listeners.forEach(listener => listener());

/** `motion.durFast` in milliseconds: the one fade, the picture's out or a map's in. */
export const FADE_MS = parseFloat(motion.durFast) * (motion.durFast.endsWith('ms') ? 1 : 1000);
/** One frame past the fade: the fallback's margin should `transitionend` never come. */
export const FADE_MARGIN_MS = 50;
/** About one frame: a shorter fade is not seen (cut instead). */
export const FRAME_MS = 17;

/**
 * The frames between a release and the fade's last painted frame that the fade's own length does
 * not count: the transition starts on the frame after the release commits, and its last frame is
 * painted on the one after it ends (measured on the rig, 2026-10-31).
 */
const FADE_LAG_MS = 2 * FRAME_MS;

/**
 * Handover criterion 4 (glass-align R2.5): a picture is gone within the cap plus one fade of its
 * capture. The cap's timer can fire late while the arriving map is built on the main thread, so a
 * fade starting near or past the cap runs over only the time left, less its start and end frames.
 * A fade on arrival, well inside the cap, gets the whole of it.
 */
export const fadeFor = (heldAt: number, startedAt: number, capMs: number = HOLD_CAP_MS): number =>
  Math.max(0, Math.min(FADE_MS, heldAt + capMs + FADE_MS - FADE_LAG_MS - startedAt));

/** Reduced motion cuts (handover R1.4, glass-align R2.3). No `matchMedia` (jsdom): not reduced. */
export const prefersReducedMotion = (): boolean => {
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/** Why a capture held nothing, as warned. */
export type CaptureFailure = 'no-2d-context' | 'canvas-unreadable';

let warnedCapture = false;
/** One warning per page load, naming the reason only: never a twin, an organisation or a pixel. */
export function warnCaptureFailed(reason: CaptureFailure): void {
  if (warnedCapture) return;
  warnedCapture = true;
  console.warn('[planHandover] capture failed', { reason });
}

/**
 * A plan map on screen and drawn offers its capture; the returned function withdraws it (only if it
 * is still the one registered, so a later map's registration is never withdrawn by an earlier one).
 */
export function registerCapture(fn: CaptureMap): () => void {
  capture = fn;
  return () => {
    if (capture === fn) capture = null;
  };
}

/** The capture registered now (the development probe). */
export const registeredCapture = (): CaptureMap | null => capture;

export const subscribeHandover = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getHandover = (): HandoverSnapshot | null => snapshot;

const clearTimers = () => {
  if (announceTimer !== null) clearTimeout(announceTimer);
  if (capTimer !== null) clearTimeout(capTimer);
  if (backstopTimer !== null) clearTimeout(backstopTimer);
  announceTimer = null;
  capTimer = null;
  backstopTimer = null;
};

/**
 * Handover R1.4 (glass-align R2.5): a picture is gone within the cap plus one fade of its capture.
 * Once a map is fading in, the map ends the hold (its `transitionend` or its own fallback). If that
 * map unmounts mid-fade with no newer join, nothing else would; so, one fallback margin past the
 * map's own last chance, the picture is cut and the map shown. A release clears it, since the layer
 * then ends the hold.
 */
const armBackstop = (heldAt: number, capMs: number) => {
  if (backstopTimer !== null) clearTimeout(backstopTimer);
  backstopTimer = setTimeout(
    () => {
      backstopTimer = null;
      release(true);
    },
    Math.max(0, heldAt + capMs + FADE_MS + 2 * FADE_MARGIN_MS - now()),
  );
};

const onResize = () => releaseHold({ immediate: true });

/** Start releasing the held picture: the layer fades it (or cuts it) and then calls `finishRelease`. */
const release = (immediate: boolean) => {
  if (!snapshot) return;
  if (snapshot.releasing && (snapshot.immediate || !immediate)) return;
  clearTimers();
  // A fade keeps listening, so a resize during it cuts the picture rather than leave it misplaced.
  if (immediate && typeof window !== 'undefined') window.removeEventListener('resize', onResize);
  snapshot = { ...snapshot, releasing: true, immediate, releasedAt: now() };
  mark('handover:released');
  notify();
};

/**
 * Glass-align R2.2, R2.5: the current generation's map starts fading in over the picture. The
 * picture stays until the map's fade ends (`finishRelease`, called by the map). Marked as a release,
 * so the rig times the picture's going the same way for either kind of map.
 */
const startReveal = () => {
  if (!snapshot || snapshot.releasing || snapshot.revealGeneration === generation) return;
  if (announceTimer !== null) clearTimeout(announceTimer);
  announceTimer = null;
  snapshot = { ...snapshot, revealGeneration: generation, revealStartedAt: now() };
  mark('handover:released');
  notify();
};

/**
 * R3.4: the cap. A map already fading in finishes by its own fade; an editor map announced but not
 * arrived is shown now, fading over only the time left (glass-align R2.5); otherwise the picture
 * fades out, as before.
 */
const onCap = () => {
  capTimer = null;
  if (!snapshot || snapshot.releasing) return;
  if (snapshot.revealGeneration === generation) return;
  if (snapshot.revealer === generation) startReveal();
  else release(false);
};

/**
 * R2, R3.1: a handed-over step change is about to commit. Holds the registered map's picture, or,
 * with one already held, keeps it under a new generation (a newer change overtook the hold: no drawn
 * map is on screen to capture). Holds nothing when no map is registered or the capture fails.
 *
 * `options` (`spec-onboarding-persistent-scene-edit-flight`, R6.5): the live viewer's Edit waits
 * longer, because onboarding loads the twin before Floor Plan's map mounts. Every other hold passes
 * none and keeps 250 ms and 1500 ms. An overtaking hold keeps the held picture's own bounds.
 */
export function holdScene({
  announceWaitMs = ANNOUNCE_WAIT_MS,
  capMs = HOLD_CAP_MS,
  origin = 'step',
  twinBid,
}: HoldOptions = {}): void {
  if (snapshot && !snapshot.releasing) {
    generation += 1;
    snapshot = { ...snapshot, generation };
    if (announceTimer !== null) clearTimeout(announceTimer);
    announceTimer = setTimeout(() => release(false), snapshot.announceWaitMs);
    // The cap counts from the capture, which an overtaking join keeps: if it has already fired (it
    // started the older map's fade), it is armed again for the time left, so the newer map is shown
    // by it too.
    if (capTimer === null) {
      capTimer = setTimeout(onCap, Math.max(0, snapshot.heldAt + snapshot.capMs - now()));
    }
    notify();
    return;
  }
  const picture = capture?.() ?? null;
  if (!picture) return;
  // A picture still fading is replaced, not stacked (R1.2).
  if (snapshot) finishRelease(snapshot.picture);
  generation += 1;
  pictureSeq += 1;
  snapshot = {
    picture,
    pictureId: pictureSeq,
    generation,
    releasing: false,
    immediate: false,
    heldAt: now(),
    capMs,
    announceWaitMs,
    origin,
    twinBid: twinBid ?? null,
    releasedAt: null,
    revealer: null,
    revealGeneration: null,
    revealStartedAt: null,
  };
  mark('handover:held');
  announceTimer = setTimeout(() => release(false), announceWaitMs);
  capTimer = setTimeout(onCap, capMs);
  armBackstop(snapshot.heldAt, capMs);
  if (typeof window !== 'undefined') window.addEventListener('resize', onResize);
  notify();
}

/**
 * R3.2, R3.3: an arriving plan map mounted. Returns the generation its arrival reports against.
 * `reveals`: an editor map, which stays invisible over the picture and fades in on arrival
 * (glass-align R2.2), rather than the picture fading out over it.
 */
export function announceArrival({ reveals = false }: { reveals?: boolean } = {}): number {
  mark('handover:announced');
  if (snapshot && !snapshot.releasing) {
    if (announceTimer !== null) {
      clearTimeout(announceTimer);
      announceTimer = null;
    }
    if (reveals && snapshot.revealer !== generation) {
      snapshot = { ...snapshot, revealer: generation };
      notify();
    }
  }
  return generation;
}

/**
 * R6.6: is a picture held, not being let go, that the live viewer's Edit made for `twinBid`?
 * Onboarding's landing does not release such a hold.
 */
export const heldEditFor = (twinBid: string | null | undefined): boolean =>
  !!twinBid &&
  !!snapshot &&
  !snapshot.releasing &&
  snapshot.origin === 'edit' &&
  snapshot.twinBid === twinBid;

/** Is a picture held and not being let go (a map mounting now is arriving into a hold)? */
export const holdInProgress = (): boolean => !!snapshot && !snapshot.releasing;

/**
 * Glass-align R2: what the map that announced into `generation` shows. `held`: whether a hold was in
 * progress as it announced; a map that mounted with none shows at once (R2.8).
 *
 * - No picture, or one being let go (a fade out, a failure, a step change that cut): as always.
 * - This generation's map has started its fade-in: fading in.
 * - Otherwise (not arrived yet, or an older map's fade under way, Overtaken): invisible.
 */
export const revealFor = (
  held: HandoverSnapshot | null,
  mapGeneration: number,
  heldAsAnnounced: boolean,
): RevealState => {
  if (!heldAsAnnounced || !held || held.releasing) return 'shown';
  return held.revealGeneration === mapGeneration ? 'revealing' : 'hidden';
};

/**
 * R3.2, R3.5: the arriving map drew and went idle at its opening camera (released with the fade), or
 * its draw failed (released at once, so the step's own failure banner is not covered).
 */
export function reportArrived(reported: number, outcome: ArrivalOutcome): void {
  // An overtaken map's report marks nothing, so the rig never times the newest join by it; the
  // newest map's late report, after the cap released the hold, is still marked.
  if (reported !== generation) return;
  mark(outcome === 'arrived' ? 'handover:arrived' : 'handover:failed');
  if (!snapshot || reported !== snapshot.generation) return;
  // Glass-align R2.2, R2.4: an editor map fades in over the picture; a failed draw is shown at once.
  if (outcome === 'arrived' && snapshot.revealer === reported) startReveal();
  else release(outcome === 'failed');
}

/**
 * Release the picture now: a step change that is not handed over, the page unmounting or its twin
 * changing (`immediate`: removed without a fade, so it never shows over another page).
 */
export function releaseHold({ immediate = false }: { immediate?: boolean } = {}): void {
  release(immediate);
}

/** The layer has faded (or cut) `picture` out: forget it and free its canvas. */
export function finishRelease(picture: HeldPicture): void {
  if (!snapshot || snapshot.picture !== picture) return;
  clearTimers();
  if (typeof window !== 'undefined') window.removeEventListener('resize', onResize);
  snapshot = null;
  picture.canvas.remove();
  // Frees the backing store promptly.
  picture.canvas.width = 0;
  picture.canvas.height = 0;
  notify();
}

/** For tests: forget everything. */
export function resetPlanHandoverForTests(): void {
  clearTimers();
  if (typeof window !== 'undefined') window.removeEventListener('resize', onResize);
  capture = null;
  generation = 0;
  snapshot = null;
  warnedCapture = false;
  listeners.clear();
}
